import React, { FC, memo, useEffect, useMemo, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { Sparkles } from "@UI/Components/Icons/Sparkles";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import useI18n from "@I18n/hooks/useI18n";
import useThemeToken from "@UI/hooks/useThemeToken";

import messages from "../../messages";

const CAPTION_HOLD_MS = 2500;
const CAPTION_FADE_MS = 300;

const PULSE_DURATION = "1.4s";
const PULSE_SCALE_KEYFRAMES = {
  "0%": { transform: "scale(1)" },
  "50%": { transform: "scale(1.125)" },
  "100%": { transform: "scale(1)" },
};

export interface IAiLoaderModalProps {
  isOpen: boolean;
  captions?: string[];
}

/** Shown while the description POST is in flight (RT-4321). */
export const AiLoaderModal: FC<IAiLoaderModalProps> = memo(
  ({ isOpen, captions }) => {
    const { formatMessage } = useI18n();
    const [pink500, orange500] = useThemeToken("colors", [
      "pink.500",
      "orange.500",
    ]);
    // 100% matches 0% so the loop has no snap — continuous back-and-forth motion.
    const sweepKeyframes = useMemo(
      () => ({
        "0%": { color: pink500 },
        "25%": { color: orange500 },
        "50%": { color: pink500 },
        "75%": { color: orange500 },
        "100%": { color: pink500 },
      }),
      [pink500, orange500]
    );
    const [captionIndex, setCaptionIndex] = useState(0);
    const [captionVisible, setCaptionVisible] = useState(true);
    const resolvedCaptions = useMemo(
      () =>
        captions ?? [
          formatMessage(messages["taskCreation.aiLoaderModal.defaultCaption"]),
        ],
      [captions, formatMessage]
    );

    useEffect(() => {
      if (resolvedCaptions.length <= 1) return undefined;

      const interval = setInterval(() => {
        setCaptionVisible(false);
        setTimeout(() => {
          setCaptionIndex((index) => (index + 1) % resolvedCaptions.length);
          setCaptionVisible(true);
        }, CAPTION_FADE_MS);
      }, CAPTION_HOLD_MS + CAPTION_FADE_MS);

      return () => clearInterval(interval);
    }, [resolvedCaptions]);

    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => undefined}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        closeButtonDisabled
        isCentered
      >
        <Box display="flex" flexDirection="column" alignItems="center" py="10" gap="8">
          <Icon
            as={Sparkles}
            boxSize="64px"
            sx={{
              "@keyframes aiLoaderSweep": sweepKeyframes,
              "@keyframes aiLoaderBreathe": PULSE_SCALE_KEYFRAMES,
              animation: `aiLoaderSweep 3s linear infinite, aiLoaderBreathe ${PULSE_DURATION} ease-in-out infinite`,
            }}
          />
          <Heading
            as="h4"
            fontSize={{ base: "xl", lg: "3xl" }}
            color="gray.800"
            textAlign="center"
            transition={`opacity ${CAPTION_FADE_MS}ms ease-in-out`}
            opacity={captionVisible ? 1 : 0}
          >
            {resolvedCaptions[captionIndex]}
          </Heading>
        </Box>
      </ResponsiveModal>
    );
  }
);

AiLoaderModal.displayName = "AiLoaderModal";
